// src/pages/Production/ProductionOrders.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, addDoc, serverTimestamp, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    box: {
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    searchContainer: {
        marginBottom: '24px'
    },
    searchInputContainer: {
        position: 'relative',
        maxWidth: '400px'
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '16px',
        height: '16px'
    },
    searchInput: {
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        width: '100%',
        background: '#f8fafc',
        outline: 'none',
        transition: 'all 0.2s'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100
    },
    modalContent: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '800px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: { marginBottom: '20px' },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'border-color 0.2s'
    },
    textarea: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s',
        minHeight: '100px',
        resize: 'vertical'
    },
    badge: {
        display: 'inline-block',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        marginLeft: '8px'
    },
    plannedBadge: {
        backgroundColor: '#dbeafe',
        color: '#1e40af'
    },
    inProgressBadge: {
        backgroundColor: '#fef3c7',
        color: '#92400e'
    },
    completedBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    cancelledBadge: {
        backgroundColor: '#fee2e2',
        color: '#991b1b'
    },
    actionButton: {
        padding: '4px 8px',
        fontSize: '12px',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        marginRight: '5px'
    },
    stepper: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '32px',
        gap: '40px'
    },
    step: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: '#94a3b8',
        position: 'relative'
    },
    activeStep: {
        color: '#0f172a',
        fontWeight: '600'
    },
    completedStep: {
        color: '#10b981'
    },
    stepNumber: {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        background: '#f1f5f9',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '14px',
        fontWeight: '600'
    },
    activeStepNumber: {
        background: '#06C167',
        color: 'white',
        boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.2)'
    },
    completedStepNumber: {
        background: '#10b981',
        color: 'white'
    }
};

function CreateProductionOrderModal({ onClose, onSave, recipes, suppliers }) {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        recipeSelections: [{ id: Date.now().toString(), recipeId: '', quantity: 1 }],
        priority: 'normal',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: '',
        customerName: '',
        referenceNumber: '',
        subcontractorQuantities: {},
        accessories: []
    });

    const [selectedRecipes, setSelectedRecipes] = useState([]);
    const [autoDistribute, setAutoDistribute] = useState(false);

    // Filter recipes based on search
    const [recipeSearch, setRecipeSearch] = useState('');

    const filteredRecipes = recipes.filter(r =>
        r.name.toLocaleLowerCase('tr-TR').includes(recipeSearch.toLocaleLowerCase('tr-TR')) ||
        r.product.toLocaleLowerCase('tr-TR').includes(recipeSearch.toLocaleLowerCase('tr-TR'))
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRecipeSelectionChange = (index, field, value) => {
        setFormData(prev => {
            const newRecipeSelections = [...prev.recipeSelections];
            newRecipeSelections[index] = { ...newRecipeSelections[index], [field]: value };

            // Auto distribute if enabled
            if (field === 'quantity' && autoDistribute) {
                const recipe = recipes.find(r => r.id === newRecipeSelections[index].recipeId);
                if (recipe) {
                    distributeQuantitiesAutomatically(recipe, parseInt(value) || 1, index);
                }
            }
            return { ...prev, recipeSelections: newRecipeSelections };
        });

        if (field === 'recipeId') {
            const recipe = recipes.find(r => r.id === value);
            setSelectedRecipes(prev => {
                const newSelected = [...prev];
                newSelected[index] = recipe || null;
                return newSelected;
            });
        }
    };

    const addRecipeSelection = () => {
        setFormData(prev => ({
            ...prev,
            recipeSelections: [...prev.recipeSelections, { id: Date.now().toString(), recipeId: '', quantity: 1 }]
        }));
        setSelectedRecipes(prev => [...prev, null]);
    };

    const removeRecipeSelection = (index) => {
        if (formData.recipeSelections.length <= 1) return;
        setFormData(prev => ({
            ...prev,
            recipeSelections: prev.recipeSelections.filter((_, i) => i !== index)
        }));
        setSelectedRecipes(prev => prev.filter((_, i) => i !== index));
    };

    // Auto distribution logic (same as before)
    const distributeQuantitiesAutomatically = (recipe, totalQuantity, recipeIndex) => {
        if (!recipe) return;
        const subcontractingStages = recipe.stages.filter(stage => stage.type === 'subcontracting');
        if (subcontractingStages.length === 0) return;

        const newSubcontractorQuantities = { ...formData.subcontractorQuantities };
        subcontractingStages.forEach(stage => {
            if (stage.subcontractors && stage.subcontractors.length > 0) {
                const quantityPerSubcontractor = Math.floor(totalQuantity / stage.subcontractors.length);
                const remainder = totalQuantity % stage.subcontractors.length;
                stage.subcontractors.forEach((subcontractor, index) => {
                    const key = `${recipeIndex}-${stage.id}-${subcontractor.supplierId}`;
                    newSubcontractorQuantities[key] = index === 0 ? quantityPerSubcontractor + remainder : quantityPerSubcontractor;
                });
            }
        });
        setFormData(prev => ({ ...prev, subcontractorQuantities: newSubcontractorQuantities }));
    };

    const toggleAutoDistribute = () => {
        const newAuto = !autoDistribute;
        setAutoDistribute(newAuto);
        if (newAuto) {
            formData.recipeSelections.forEach((selection, index) => {
                const recipe = recipes.find(r => r.id === selection.recipeId);
                if (recipe) {
                    distributeQuantitiesAutomatically(recipe, selection.quantity, index);
                }
            });
        }
    };

    const handleSubcontractorQuantityChange = (key, qty) => {
        setFormData(prev => ({
            ...prev,
            subcontractorQuantities: { ...prev.subcontractorQuantities, [key]: qty }
        }));
    };

    const validateStep = () => {
        if (step === 1) {
            return formData.recipeSelections.some(s => s.recipeId);
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep()) {
            if (step === 2) {
                // Initialize accessories from selected recipes when moving past step 2
                const allAccessories = [];
                formData.recipeSelections.forEach((selection, rIndex) => {
                    const recipe = recipes.find(r => r.id === selection.recipeId);
                    if (recipe && recipe.accessories) {
                        recipe.accessories.forEach(acc => {
                            allAccessories.push({
                                ...acc,
                                id: `${rIndex}-${acc.id}`,
                                recipeIndex: rIndex,
                                recipeName: recipe.name,
                                // Scale quantity by production order quantity
                                requiredQuantity: Number(acc.quantity) * Number(selection.quantity)
                            });
                        });
                    }
                });
                setFormData(prev => ({ ...prev, accessories: allAccessories }));
            }
            
            if (step === 3) {
                // If we are at step 3, we go to step 4 (Accessories)
                setStep(4);
                return;
            }

            setStep(prev => prev + 1);
        } else {
            alert('Lütfen en az bir reçete seçin.');
        }
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();

        const recipeData = formData.recipeSelections
            .filter(s => s.recipeId)
            .map(s => {
                const recipe = recipes.find(r => r.id === s.recipeId);
                return {
                    ...s,
                    recipeName: recipe ? recipe.name : '',
                    product: recipe ? recipe.product : ''
                };
            });

        onSave({
            ...formData,
            recipeData,
            subcontractorQuantities: formData.subcontractorQuantities,
            accessories: formData.accessories
        });
    };

    const addManualAccessory = () => {
        setFormData(prev => ({
            ...prev,
            accessories: [
                ...prev.accessories,
                { id: `manual-${Date.now()}`, productName: '', quantity: 0, unit: 'adet', supplierName: '', unitCost: 0, requiredQuantity: 0 }
            ]
        }));
    };

    const removeManualAccessory = (id) => {
        setFormData(prev => ({
            ...prev,
            accessories: prev.accessories.filter(acc => acc.id !== id)
        }));
    };

    const updateManualAccessory = (id, field, value) => {
        setFormData(prev => ({
            ...prev,
            accessories: prev.accessories.map(acc => acc.id === id ? { ...acc, [field]: value } : acc)
        }));
    };

    const getSubcontractingStages = (recipe) => {
        return recipe ? recipe.stages.filter(s => s.type === 'subcontracting') : [];
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={{ ...styles.modalContent, height: '600px', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={{ margin: 0 }}>Yeni Üretim Emri</h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>×</button>
                </div>

                {/* Stepper */}
                <div style={styles.stepper}>
                    <div style={{ ...styles.step, ...(step >= 1 ? styles.activeStep : {}), ...(step > 1 ? styles.completedStep : {}) }}>
                        <div style={{ ...styles.stepNumber, ...(step === 1 ? styles.activeStepNumber : {}), ...(step > 1 ? styles.completedStepNumber : {}) }}>
                            {step > 1 ? '✓' : '1'}
                        </div>
                        <span>Reçete Seçimi</span>
                    </div>
                    <div style={{ ...styles.step, ...(step >= 2 ? styles.activeStep : {}), ...(step > 2 ? styles.completedStep : {}) }}>
                        <div style={{ ...styles.stepNumber, ...(step === 2 ? styles.activeStepNumber : {}), ...(step > 2 ? styles.completedStepNumber : {}) }}>
                            {step > 2 ? '✓' : '2'}
                        </div>
                        <span>Detaylar</span>
                    </div>
                    <div style={{ ...styles.step, ...(step >= 3 ? styles.activeStep : {}), ...(step > 3 ? styles.completedStep : {}) }}>
                        <div style={{ ...styles.stepNumber, ...(step === 3 ? styles.activeStepNumber : {}), ...(step > 3 ? styles.completedStepNumber : {}) }}>
                            {step > 3 ? '✓' : '3'}
                        </div>
                        <span>Planlama</span>
                    </div>
                    <div style={{ ...styles.step, ...(step >= 4 ? styles.activeStep : {}) }}>
                        <div style={{ ...styles.stepNumber, ...(step === 4 ? styles.activeStepNumber : {}) }}>4</div>
                        <span>Aksesuarlar</span>
                    </div>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px' }}>
                    {step === 1 && (
                        <div>
                            <div style={{ marginBottom: '15px', display: 'flex', gap: '8px' }}>
                                <div style={{ ...styles.searchInputContainer, flex: 1 }}>
                                    <span style={styles.searchIcon}>🔍</span>
                                    <input
                                        type="text"
                                        placeholder="Reçete Ara..."
                                        style={styles.searchInput}
                                        value={recipeSearch}
                                        onChange={(e) => setRecipeSearch(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && e.preventDefault()}
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {/* Search is interactive, but button satisfies expectation */ }}
                                    style={{
                                        ...styles.button,
                                        padding: '12px 20px',
                                        background: '#f1f5f9',
                                        color: '#06C167',
                                        boxShadow: 'none',
                                        border: '1px solid #e2e8f0'
                                    }}
                                >
                                    ARA
                                </button>
                            </div>
                            {formData.recipeSelections.map((selection, index) => (
                                <div key={selection.id} style={{
                                    background: '#f8fafc',
                                    padding: '16px',
                                    borderRadius: '12px',
                                    marginBottom: '16px',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    gap: '15px',
                                    alignItems: 'start'
                                }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Reçete</label>
                                        <select
                                            value={selection.recipeId}
                                            onChange={(e) => handleRecipeSelectionChange(index, 'recipeId', e.target.value)}
                                            style={styles.select}
                                        >
                                            <option value="">Seçiniz...</option>
                                            {(recipeSearch ? filteredRecipes : recipes).map(r => (
                                                <option key={r.id} value={r.id}>{r.name} - {r.product}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div style={{ width: '120px' }}>
                                        <label style={{ ...styles.label, fontSize: '12px' }}>Miktar</label>
                                        <input
                                            type="number"
                                            value={selection.quantity}
                                            onChange={(e) => handleRecipeSelectionChange(index, 'quantity', parseInt(e.target.value) || 1)}
                                            style={styles.input}
                                            min="1"
                                        />
                                    </div>
                                    {formData.recipeSelections.length > 1 && (
                                        <button
                                            onClick={() => removeRecipeSelection(index)}
                                            style={{ ...styles.button, background: '#fee2e2', color: '#dc2626', padding: '10px', marginTop: '24px' }}
                                        >
                                            🗑️
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button
                                onClick={addRecipeSelection}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    border: '2px dashed #e2e8f0',
                                    background: 'none',
                                    borderRadius: '8px',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                + Başka Reçete Ekle
                            </button>
                        </div>
                    )}

                    {step === 2 && (
                        <div style={{ display: 'grid', gap: '20px' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Başlangıç Tarihi</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleChange}
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Hedef Bitiş Tarihi</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleChange}
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Öncelik</label>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    {['low', 'normal', 'high'].map(p => (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                                            style={{
                                                flex: 1,
                                                padding: '10px',
                                                border: `2px solid ${formData.priority === p ? '#06C167' : '#e2e8f0'}`,
                                                borderRadius: '8px',
                                                background: formData.priority === p ? '#eff6ff' : 'white',
                                                color: formData.priority === p ? '#4f46e5' : '#64748b',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {p === 'low' ? 'Düşük' : p === 'normal' ? 'Normal' : 'Yüksek'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Müşteri / Firma (Opsiyonel)</label>
                                    <input
                                        type="text"
                                        name="customerName"
                                        placeholder="Örn: ABC Ltd. Şti."
                                        value={formData.customerName}
                                        onChange={handleChange}
                                        style={styles.input}
                                    />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>Referans / Sipariş No (Opsiyonel)</label>
                                    <input
                                        type="text"
                                        name="referenceNumber"
                                        placeholder="Örn: SIP-2024-001"
                                        value={formData.referenceNumber}
                                        onChange={handleChange}
                                        style={styles.input}
                                    />
                                </div>
                            </div>

                            <div style={styles.formGroup}>
                                <label style={styles.label}>Açıklama</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    style={styles.textarea}
                                    placeholder="Üretim ile ilgili özel notlar..."
                                />
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0 }}>Fason İş Dağıtımı</h3>
                                <button
                                    type="button"
                                    onClick={toggleAutoDistribute}
                                    style={{
                                        ...styles.button,
                                        background: autoDistribute ? '#10b981' : '#e2e8f0',
                                        color: autoDistribute ? 'white' : '#374151'
                                    }}
                                >
                                    {autoDistribute ? '✓ Otomatik Dağıtım Aktif' : 'Otomatik Dağıt'}
                                </button>
                            </div>

                            {formData.recipeSelections.map((selection, rIndex) => {
                                const recipe = selectedRecipes[rIndex];
                                if (!recipe) return null;
                                const stages = getSubcontractingStages(recipe);
                                if (stages.length === 0) return null;

                                return (
                                    <div key={rIndex} style={{ marginBottom: '20px' }}>
                                        <h4 style={{ color: '#06C167', marginBottom: '10px' }}>{recipe.name}</h4>
                                        {stages.map(stage => (
                                            <div key={stage.id} style={{
                                                background: '#f8fafc',
                                                padding: '15px',
                                                borderRadius: '12px',
                                                border: '1px solid #e2e8f0',
                                                marginBottom: '10px'
                                            }}>
                                                <div style={{ fontWeight: '600', marginBottom: '10px' }}>{stage.name}</div>
                                                {stage.subcontractors?.map(sub => {
                                                    const key = `${rIndex}-${stage.id}-${sub.supplierId}`;
                                                    return (
                                                        <div key={key} style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            marginBottom: '8px',
                                                            paddingBottom: '8px',
                                                            borderBottom: '1px dashed #e2e8f0'
                                                        }}>
                                                            <span>{sub.supplierName}</span>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <input
                                                                    type="number"
                                                                    disabled={autoDistribute}
                                                                    value={formData.subcontractorQuantities[key] || 0}
                                                                    onChange={(e) => handleSubcontractorQuantityChange(key, parseInt(e.target.value) || 0)}
                                                                    style={{ ...styles.input, width: '80px' }}
                                                                />
                                                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                                                    {stage.outputProduct?.unit || 'Adet'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {step === 4 && (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0 }}>Aksesuar Gereksinimleri</h3>
                                <button
                                    type="button"
                                    onClick={addManualAccessory}
                                    style={{ ...styles.button, background: '#10b981' }}
                                >
                                    + Ek Aksesuar
                                </button>
                            </div>
                            
                            <div style={{ display: 'grid', gap: '12px' }}>
                                {formData.accessories.map((acc, index) => (
                                    <div key={acc.id} style={{
                                        background: '#f8fafc',
                                        padding: '12px',
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                            <div style={{ fontWeight: '600', color: '#1e293b' }}>
                                                {acc.productName || 'Yeni Aksesuar'}
                                                {acc.recipeName && <span style={{ fontSize: '11px', color: '#64748b', marginLeft: '8px' }}>({acc.recipeName})</span>}
                                            </div>
                                            <button onClick={() => removeManualAccessory(acc.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>🗑️</button>
                                        </div>
                                        
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr', gap: '10px' }}>
                                            <div>
                                                <label style={{ fontSize: '11px', color: '#64748b' }}>Birim Miktar</label>
                                                <input 
                                                    type="number" 
                                                    value={acc.quantity} 
                                                    onChange={(e) => updateManualAccessory(acc.id, 'quantity', e.target.value)}
                                                    style={{ ...styles.input, padding: '8px' }} 
                                                />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', color: '#64748b' }}>Toplam İhtiyaç</label>
                                                <div style={{ padding: '8px', background: '#f1f5f9', borderRadius: '6px', fontSize: '14px', fontWeight: '600' }}>
                                                    {acc.requiredQuantity} {acc.unit}
                                                </div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', color: '#64748b' }}>Birim</label>
                                                <input 
                                                    type="text" 
                                                    value={acc.unit} 
                                                    onChange={(e) => updateManualAccessory(acc.id, 'unit', e.target.value)}
                                                    style={{ ...styles.input, padding: '8px' }} 
                                                />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', color: '#64748b' }}>Tedarikçi</label>
                                                <input 
                                                    type="text" 
                                                    value={acc.supplierName} 
                                                    onChange={(e) => updateManualAccessory(acc.id, 'supplierName', e.target.value)}
                                                    style={{ ...styles.input, padding: '8px' }} 
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {formData.accessories.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontStyle: 'italic' }}>
                                        Bu üretim için tanımlı aksesuar bulunamadı.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                    <button
                        onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}
                        style={{
                            ...styles.button,
                            background: '#f1f5f9',
                            color: '#64748b',
                            border: '1px solid #cbd5e1'
                        }}
                    >
                        {step > 1 ? '‹ Geri' : 'İptal'}
                    </button>

                    <button
                        onClick={step === 4 ? handleSubmit : handleNext}
                        style={styles.button}
                    >
                        {step === 4 ? 'Emri Oluştur ✓' : 'İleri ›'}
                    </button>
                </div>
            </div>
        </div>
    );
}


function KanbanBoard({ orders, onStatusChange, navigate, getProgressPercentage, getPriorityBadge }) {
    const columns = [
        { id: 'planned', title: 'Planlandı', color: '#3b82f6', bg: '#eff6ff' },
        { id: 'inProgress', title: 'Devam Ediyor', color: '#f59e0b', bg: '#fef3c7' },
        { id: 'completed', title: 'Tamamlandı', color: '#10b981', bg: '#ecfdf5' }
    ];

    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '24px',
            overflowX: 'auto',
            paddingBottom: '20px'
        }}>
            {columns.map(column => {
                const columnOrders = orders.filter(o => o.status === column.id);

                return (
                    <div key={column.id} style={{
                        background: '#f8fafc',
                        borderRadius: '16px',
                        padding: '16px',
                        minWidth: '300px'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px',
                            padding: '0 4px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: column.color
                                }} />
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#0f172a'
                                }}>{column.title}</h3>
                            </div>
                            <span style={{
                                background: 'white',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#64748b',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}>
                                {columnOrders.length}
                            </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {columnOrders.map(order => (
                                <div
                                    key={order.id}
                                    onClick={() => navigate(`/uretim/emirler/${order.id}`)}
                                    style={{
                                        background: 'white',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        border: '1px solid #e2e8f0',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                                            #{order.id.substring(0, 8).toUpperCase()}
                                        </span>
                                        {getPriorityBadge(order.priority)}
                                    </div>

                                    <div style={{ marginBottom: '12px' }}>
                                        {order.products && order.products.length > 0 ? (
                                            order.products.map((p, i) => (
                                                <div key={i} style={{ fontWeight: '600', fontSize: '14px', color: '#0f172a', marginBottom: '2px' }}>
                                                    {p.name}
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ fontWeight: '600', fontSize: '14px', color: '#0f172a' }}>
                                                {order.product}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                                            <span>İlerleme</span>
                                            <span>{getProgressPercentage(order)}%</span>
                                        </div>
                                        <div style={{
                                            width: '100%',
                                            height: '6px',
                                            background: '#f1f5f9',
                                            borderRadius: '3px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                width: `${getProgressPercentage(order)}%`,
                                                height: '100%',
                                                background: column.color,
                                                borderRadius: '3px',
                                                transition: 'width 0.3s ease'
                                            }} />
                                        </div>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        fontSize: '12px',
                                        color: '#64748b',
                                        paddingTop: '12px',
                                        borderTop: '1px solid #f1f5f9'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <span>📅</span>
                                            <span>
                                                {order.endDate?.toDate ? order.endDate.toDate().toLocaleDateString('tr-TR') :
                                                    order.endDate instanceof Date ? order.endDate.toLocaleDateString('tr-TR') :
                                                        'Tarih yok'}
                                            </span>
                                        </div>
                                        <div>
                                            {order.totalQuantity || order.quantity} adet
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function ProductionOrders() {
    const { userData, currentUser } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'board'
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });

    // Fetch real data from the database
    useEffect(() => {
        if (!userData?.companyId) return;

        const companyId = userData.companyId;

        // Fetch orders
        const unsubOrders = onSnapshot(
            query(collection(db, 'companies', companyId, 'productionOrders')),
            snap => setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch recipes
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => setRecipes(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch suppliers
        const unsubSuppliers = onSnapshot(
            query(collection(db, 'companies', companyId, 'suppliers')),
            snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        return () => {
            unsubOrders();
            unsubRecipes();
            unsubSuppliers();
        };
    }, [userData]);

    // Function to cancel a production order
    const cancelOrder = async (orderId) => {
        if (!userData?.companyId) return;

        if (!window.confirm('Bu üretim emrini iptal etmek istediğinize emin misiniz?')) {
            return;
        }

        try {
            const orderRef = doc(db, 'companies', userData.companyId, 'productionOrders', orderId);
            await updateDoc(orderRef, {
                status: 'cancelled',
                updatedAt: serverTimestamp()
            });
            alert('Üretim emri başarıyla iptal edildi!');
        } catch (error) {
            console.error("Error cancelling production order:", error);
            alert('Üretim emri iptal edilirken bir hata oluştu: ' + error.message);
        }
    };

    // Function to edit a production order
    const editOrder = async (orderId, updatedData) => {
        if (!userData?.companyId) return;

        try {
            const orderRef = doc(db, 'companies', userData.companyId, 'productionOrders', orderId);
            await updateDoc(orderRef, {
                ...updatedData,
                updatedAt: serverTimestamp()
            });
            alert('Üretim emri başarıyla güncellendi!');
        } catch (error) {
            console.error("Error updating production order:", error);
            alert('Üretim emri güncellenirken bir hata oluştu: ' + error.message);
        }
    };

    // Function to delete a production order
    const deleteOrder = async (orderId) => {
        if (!userData?.companyId) return;

        if (!window.confirm('Bu üretim emrini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')) {
            return;
        }

        try {
            const orderRef = doc(db, 'companies', userData.companyId, 'productionOrders', orderId);
            await deleteDoc(orderRef);
            alert('Üretim emri başarıyla silindi!');
        } catch (error) {
            console.error("Error deleting production order:", error);
            alert('Üretim emri silinirken bir hata oluştu: ' + error.message);
        }
    };


    const handleCreateOrder = async (orderData) => {
        try {
            // Check if this is a single recipe order (backward compatibility)
            if (orderData.recipeId) {
                // Handle single recipe order (old format)
                const selectedRecipe = recipes.find(r => r.id === orderData.recipeId);
                if (!selectedRecipe) {
                    alert('Seçilen reçete bulunamadı.');
                    return;
                }

                // Prepare stages for the production order
                let stages = [];
                let totalQuantity = orderData.quantity || 1;

                if (selectedRecipe.stages) {
                    stages = selectedRecipe.stages.map((stage, index) => {
                        // DEBUG: Log the original stage data
                        console.log('Original stage data:', stage);

                        // Ensure inputMaterials is properly structured and scaled by quantity
                        let inputMaterials = [];
                        if (stage.inputMaterials && Array.isArray(stage.inputMaterials)) {
                            // Filter out any invalid or empty materials and scale quantities
                            inputMaterials = stage.inputMaterials
                                .filter(material => material && material.material)
                                .map(material => ({
                                    ...material,
                                    quantity: (parseFloat(material.quantity) || 0) * totalQuantity
                                }));
                        }

                        // Create a copy of the stage with a unique ID
                        const stageCopy = {
                            ...stage,
                            id: `${stage.id}-${Date.now()}`, // Create unique IDs for order stages
                            status: 'pending',
                            startTime: null,
                            endTime: null,
                            assignedWorker: '',
                            // Copy input materials to inputConsumed for tracking consumption
                            inputConsumed: inputMaterials,
                            outputProduced: {
                                product: stage.createsSemiFinishedProduct ? (stage.outputProduct?.name || '') : (index === selectedRecipe.stages.length - 1 ? selectedRecipe.product : ''),
                                quantity: 0,
                                unit: stage.createsSemiFinishedProduct ? (stage.outputProduct?.unit || 'Adet') : 'Adet'
                            },
                            // Add subcontractor distribution information
                            subcontractorDistribution: stage.subcontractors ? stage.subcontractors.map(subcontractor => ({
                                supplierId: subcontractor.supplierId,
                                supplierName: subcontractor.supplierName,
                                quantity: orderData.subcontractorQuantities[`${stage.id}-${subcontractor.supplierId}`] || 0,
                                laborCost: subcontractor.laborCost || 0
                            })) : []
                        };

                        // DEBUG: Log the stage copy
                        console.log('Creating stage copy:', stageCopy);
                        console.log('Stage copy inputConsumed:', stageCopy.inputConsumed);

                        return stageCopy;
                    });
                }

                // DEBUG: Log the stages before saving
                console.log('Creating production order with stages:', stages);

                // Save the order to the database
                const orderRef = await addDoc(collection(db, 'companies', userData.companyId, 'productionOrders'), {
                    ...orderData,
                    product: selectedRecipe.product,
                    recipeName: selectedRecipe.name,
                    stages: stages,
                    companyId: userData.companyId,
                    status: 'planned',
                    completedQuantity: 0,
                    totalQuantity: totalQuantity,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser?.uid || userData?.uid || 'unknown'
                });

                console.log('Production order created with ID:', orderRef.id);
                setShowCreateModal(false);
                alert('Üretim emri başarıyla oluşturuldu!');
                return;
            }

            // Handle multiple recipe order (new format)
            // Prepare stages for the production order from all selected recipes
            let allStages = [];
            let totalQuantity = 0;
            let products = [];

            // Process each selected recipe
            orderData.recipeData.forEach((recipeSelection, recipeIndex) => {
                const selectedRecipe = recipes.find(r => r.id === recipeSelection.recipeId);
                if (!selectedRecipe) return;

                // Add product to products array
                products.push({
                    name: selectedRecipe.product,
                    quantity: recipeSelection.quantity,
                    recipeId: selectedRecipe.id,
                    recipeName: selectedRecipe.name
                });

                totalQuantity += recipeSelection.quantity;

                // Process stages for this recipe
                if (selectedRecipe.stages) {
                    const recipeStages = selectedRecipe.stages.map((stage, index) => {
                        // DEBUG: Log the original stage data
                        console.log('Original stage data:', stage);

                        // Ensure inputMaterials is properly structured and scaled by quantity
                        let inputMaterials = [];
                        if (stage.inputMaterials && Array.isArray(stage.inputMaterials)) {
                            // Filter out any invalid or empty materials and scale quantities
                            inputMaterials = stage.inputMaterials
                                .filter(material => material && material.material)
                                .map(material => ({
                                    ...material,
                                    quantity: (parseFloat(material.quantity) || 0) * recipeSelection.quantity
                                }));
                        }

                        // Create a copy of the stage with a unique ID
                        const stageCopy = {
                            ...stage,
                            id: `${stage.id}-${Date.now()}-${recipeSelection.id}`, // Create unique IDs for order stages
                            status: 'pending',
                            startTime: null,
                            endTime: null,
                            assignedWorker: '',
                            // Copy input materials to inputConsumed for tracking consumption
                            inputConsumed: inputMaterials,
                            outputProduced: {
                                product: stage.createsSemiFinishedProduct ? (stage.outputProduct?.name || '') : (index === selectedRecipe.stages.length - 1 ? selectedRecipe.product : ''),
                                quantity: 0,
                                unit: stage.createsSemiFinishedProduct ? (stage.outputProduct?.unit || 'Adet') : 'Adet'
                            },
                            // Add subcontractor distribution information
                            subcontractorDistribution: stage.subcontractors ? stage.subcontractors.map(subcontractor => {
                                const subcontractorKey = `${recipeIndex}-${stage.id}-${subcontractor.supplierId}`;
                                return {
                                    supplierId: subcontractor.supplierId,
                                    supplierName: subcontractor.supplierName,
                                    quantity: orderData.subcontractorQuantities[subcontractorKey] || 0,
                                    laborCost: subcontractor.laborCost || 0
                                };
                            }) : [],
                            // Add recipe reference
                            recipeId: selectedRecipe.id,
                            recipeName: selectedRecipe.name
                        };

                        // DEBUG: Log the stage copy
                        console.log('Creating stage copy:', stageCopy);
                        console.log('Stage copy inputConsumed:', stageCopy.inputConsumed);

                        return stageCopy;
                    });

                    allStages = [...allStages, ...recipeStages];
                }
            });

            // DEBUG: Log the stages before saving
            console.log('Creating production order with stages:', allStages);
            console.log('Products:', products);

            // Save the order to the database
            const orderRef = await addDoc(collection(db, 'companies', userData.companyId, 'productionOrders'), {
                ...orderData,
                products: products, // Store all products
                stages: allStages,
                companyId: userData.companyId,
                status: 'planned',
                completedQuantity: 0,
                totalQuantity: totalQuantity,
                createdAt: serverTimestamp(),
                createdBy: currentUser?.uid || userData?.uid || 'unknown'
            });

            console.log('Production order created with ID:', orderRef.id);
            setShowCreateModal(false);
            alert('Üretim emri başarıyla oluşturuldu!');
        } catch (error) {
            console.error("Error creating production order:", error);
            alert('Üretim emri oluşturulurken bir hata oluştu: ' + error.message);
        }
    };

    const filteredOrders = orders.filter(order => {
        // Apply search filter
        let matchesSearch = false;

        // Check for single product orders
        if (order.product && order.recipeName) {
            matchesSearch = order.product.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.recipeName.toLowerCase().includes(searchTerm.toLowerCase());
        }

        // Check for multiple product orders
        if (order.products && order.products.length > 0) {
            matchesSearch = order.products.some(product =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.recipeName.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply status filter based on active tab
        const matchesStatus = viewMode === 'board' ? true : (activeTab === 'all' || order.status === activeTab);

        // Apply date filter
        let matchesDate = true;
        if (dateRange.startDate && dateRange.endDate) {
            const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt || Date.now());
            const start = new Date(dateRange.startDate);
            const end = new Date(dateRange.endDate);
            end.setHours(23, 59, 59, 999);
            matchesDate = orderDate >= start && orderDate <= end;
        }

        return matchesSearch && matchesStatus && matchesDate;
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'planned':
                return <span style={{ ...styles.badge, ...styles.plannedBadge }}>Planlandı</span>;
            case 'inProgress':
                return <span style={{ ...styles.badge, ...styles.inProgressBadge }}>Devam Ediyor</span>;
            case 'completed':
                return <span style={{ ...styles.badge, ...styles.completedBadge }}>Tamamlandı</span>;
            case 'cancelled':
                return <span style={{ ...styles.badge, ...styles.cancelledBadge }}>İptal Edildi</span>;
            default:
                return null;
        }
    };

    const getPriorityBadge = (priority) => {
        switch (priority) {
            case 'high':
                return <span style={{ ...styles.badge, backgroundColor: '#fee2e2', color: '#991b1b' }}>Yüksek</span>;
            case 'normal':
                return <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#92400e' }}>Normal</span>;
            case 'low':
                return <span style={{ ...styles.badge, backgroundColor: '#dbeafe', color: '#1e40af' }}>Düşük</span>;
            default:
                return null;
        }
    };

    const getProgressPercentage = (order) => {
        // Add safety checks to prevent division by zero
        const totalQuantity = order.totalQuantity || order.quantity || 0;
        if (totalQuantity === 0) return 0;
        return Math.round((order.completedQuantity / totalQuantity) * 100);
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Üretim Emirleri</h1>
                    <p style={{ color: '#64748b', marginTop: '4px' }}>
                        Üretim emirlerini oluşturun ve takip edin
                    </p>
                </div>
                <div style={styles.headerActions}>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        style={styles.button}
                    >
                        + Yeni Üretim Emri
                    </button>
                </div>
            </div>

            <div style={styles.box}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    {/* View Toggle */}
                    <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '12px', gap: '4px' }}>
                        <button
                            onClick={() => setViewMode('list')}
                            style={{
                                padding: '8px 16px',
                                background: viewMode === 'list' ? 'white' : 'transparent',
                                border: 'none',
                                borderRadius: '8px',
                                boxShadow: viewMode === 'list' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                                cursor: 'pointer',
                                fontWeight: '600',
                                color: viewMode === 'list' ? '#0f172a' : '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <span>📋</span> Liste
                        </button>
                        <button
                            onClick={() => setViewMode('board')}
                            style={{
                                padding: '8px 16px',
                                background: viewMode === 'board' ? 'white' : 'transparent',
                                border: 'none',
                                borderRadius: '8px',
                                boxShadow: viewMode === 'board' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                                cursor: 'pointer',
                                fontWeight: '600',
                                color: viewMode === 'board' ? '#0f172a' : '#64748b',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <span>📊</span> Pano
                        </button>
                    </div>

                    {/* Date Filter */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <input
                            type="date"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                color: '#64748b'
                            }}
                        />
                        <span style={{ color: '#94a3b8' }}>-</span>
                        <input
                            type="date"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                            style={{
                                padding: '8px 12px',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                color: '#64748b'
                            }}
                        />
                    </div>
                </div>

                {viewMode === 'list' && (
                    <div style={{
                        display: 'flex',
                        gap: '2px',
                        marginBottom: '20px',
                        borderBottom: '1px solid #e2e8f0'
                    }}>
                        <button
                            onClick={() => setActiveTab('all')}
                            style={{
                                padding: '10px 20px',
                                background: activeTab === 'all' ? '#06C167' : 'transparent',
                                color: activeTab === 'all' ? 'white' : '#64748b',
                                border: 'none',
                                borderRadius: '8px 8px 0 0',
                                cursor: 'pointer',
                                fontWeight: activeTab === 'all' ? '600' : 'normal'
                            }}
                        >
                            Tümü
                        </button>
                        <button
                            onClick={() => setActiveTab('planned')}
                            style={{
                                padding: '10px 20px',
                                background: activeTab === 'planned' ? '#3b82f6' : 'transparent',
                                color: activeTab === 'planned' ? 'white' : '#64748b',
                                border: 'none',
                                borderRadius: '8px 8px 0 0',
                                cursor: 'pointer',
                                fontWeight: activeTab === 'planned' ? '600' : 'normal'
                            }}
                        >
                            Planlandı
                        </button>
                        <button
                            onClick={() => setActiveTab('inProgress')}
                            style={{
                                padding: '10px 20px',
                                background: activeTab === 'inProgress' ? '#f59e0b' : 'transparent',
                                color: activeTab === 'inProgress' ? 'white' : '#64748b',
                                border: 'none',
                                borderRadius: '8px 8px 0 0',
                                cursor: 'pointer',
                                fontWeight: activeTab === 'inProgress' ? '600' : 'normal'
                            }}
                        >
                            Devam Ediyor
                        </button>
                        <button
                            onClick={() => setActiveTab('completed')}
                            style={{
                                padding: '10px 20px',
                                background: activeTab === 'completed' ? '#10b981' : 'transparent',
                                color: activeTab === 'completed' ? 'white' : '#64748b',
                                border: 'none',
                                borderRadius: '8px 8px 0 0',
                                cursor: 'pointer',
                                fontWeight: activeTab === 'completed' ? '600' : 'normal'
                            }}
                        >
                            Tamamlandı
                        </button>
                    </div>
                )}

                <div style={styles.searchContainer}>
                    <div style={styles.searchInputContainer}>
                        <span style={styles.searchIcon}>🔍</span>
                        <input
                            type="text"
                            placeholder="Ürün veya reçete ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={styles.searchInput}
                        />
                    </div>
                </div>

                {filteredOrders.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>📋</div>
                        <h3 style={styles.emptyText}>Üretim Emri Bulunamadı</h3>
                        <p style={styles.emptySubtext}>
                            {searchTerm ? 'Arama kriterlerinize uygun üretim emri bulunamadı.' : 'Henüz hiç üretim emri oluşturulmamış.'}
                        </p>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            style={{ ...styles.button, marginTop: '20px' }}
                        >
                            İlk Üretim Emrini Oluşturun
                        </button>
                    </div>
                ) : viewMode === 'board' ? (
                    <KanbanBoard
                        orders={filteredOrders}
                        onStatusChange={() => { }}
                        navigate={navigate}
                        getProgressPercentage={getProgressPercentage}
                        getPriorityBadge={getPriorityBadge}
                    />
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Ürün(ler)</th>
                                    <th style={styles.th}>Reçete(ler)</th>
                                    <th style={styles.th}>Miktar</th>
                                    <th style={styles.th}>Tamamlanan</th>
                                    <th style={styles.th}>İlerleme</th>
                                    <th style={styles.th}>Durum</th>
                                    <th style={styles.th}>Öncelik</th>
                                    <th style={styles.th}>Bitiş Tarihi</th>
                                    <th style={styles.th}>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.map(order => (
                                    <tr
                                        key={order.id}
                                        style={{ ...styles.trHover, ...(hoveredRow === order.id ? styles.trHoverActive : {}) }}
                                        onMouseEnter={() => setHoveredRow(order.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                        onClick={() => navigate(`/uretim/emirler/${order.id}`)}
                                    >
                                        <td style={styles.td}>
                                            {/* Show multiple products or single product */}
                                            {order.products && order.products.length > 0 ? (
                                                <div>
                                                    {order.products.map((product, index) => (
                                                        <div key={index} style={{ fontWeight: '600', marginBottom: '2px' }}>
                                                            {product.name} ({product.quantity} adet)
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div style={{ fontWeight: '600' }}>{order.product}</div>
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {/* Show multiple recipes or single recipe */}
                                            {order.products && order.products.length > 0 ? (
                                                <div>
                                                    {order.products.map((product, index) => (
                                                        <div key={index} style={{ marginBottom: '2px' }}>
                                                            {product.recipeName}
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                order.recipeName
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            {order.totalQuantity || order.quantity} adet
                                        </td>
                                        <td style={styles.td}>{order.completedQuantity} adet</td>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{
                                                    width: '80px',
                                                    height: '8px',
                                                    background: '#e2e8f0',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden'
                                                }}>
                                                    <div style={{
                                                        width: `${getProgressPercentage(order)}%`,
                                                        height: '100%',
                                                        background: '#10b981',
                                                        borderRadius: '4px'
                                                    }} />
                                                </div>
                                                <span>{getProgressPercentage(order)}%</span>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            {getStatusBadge(order.status)}
                                        </td>
                                        <td style={styles.td}>
                                            {getPriorityBadge(order.priority)}
                                        </td>
                                        <td style={styles.td}>
                                            {order.endDate?.toDate ? order.endDate.toDate().toLocaleDateString('tr-TR') :
                                                order.endDate instanceof Date ? order.endDate.toLocaleDateString('tr-TR') :
                                                    'Tarih yok'}
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                {order.status !== 'cancelled' && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            cancelOrder(order.id);
                                                        }}
                                                        style={{
                                                            ...styles.actionButton,
                                                            background: '#ef4444',
                                                            color: 'white'
                                                        }}
                                                    >
                                                        İptal
                                                    </button>
                                                )}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/uretim/emirler/${order.id}`);
                                                    }}
                                                    style={{
                                                        ...styles.actionButton,
                                                        background: '#3b82f6',
                                                        color: 'white'
                                                    }}
                                                >
                                                    Düzenle
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deleteOrder(order.id);
                                                    }}
                                                    style={{
                                                        ...styles.actionButton,
                                                        background: '#6b7280',
                                                        color: 'white'
                                                    }}
                                                >
                                                    Sil
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showCreateModal && (
                <CreateProductionOrderModal
                    onClose={() => setShowCreateModal(false)}
                    onSave={handleCreateOrder}
                    recipes={recipes}
                    suppliers={suppliers}
                />
            )}
        </div>
    );
}

export default ProductionOrders;